import React, { useState, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { Modal, Form, Select, DatePicker, Tooltip } from 'antd'
import { MinusOutlined } from '@ant-design/icons'
import { FormCard, FormItem, Heading, Input, TextArea } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import Disposal from '../iconsV2/Disposal'
import { Button, Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'

type DisposalModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  onCancel: () => void
  onSave: (data: DisposalData) => void
}

export type DisposalData = {
  description: string
  disposedStock: number
  newStock: number
  hasDiscrepancy: boolean
  discrepancyReason: string
  actionedBy: string
  actionedDate: string
  disposalMethod: string
  additionalDetails: boolean
  media?: File[]
}

const DisposalModal: React.FC<DisposalModalProps> = ({
  open,
  medicationName,
  expectedStock,
  onCancel,
  onSave
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const [form] = Form.useForm()
  const [disposedStock, setDisposedStock] = useState<number | null>(null)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)
  const [discrepancyReason, setDiscrepancyReason] = useState<string>('')
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false)
  const [fileList, setFileList] = useState<any[]>([])

  useEffect(() => {
    if (!open) {
      setDisposedStock(null)
      setAdjustmentError(null)
      setDiscrepancyReason('')
      setShowAdditionalDetails(false)
      setFileList([])
      form.resetFields()
    }
  }, [open, form])

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const handleDisposedStockChange = (value: string) => {
    if (!value || value === '') {
      setDisposedStock(null)
      setAdjustmentError(null)
      setDiscrepancyReason('')
      return
    }

    const numVal = Number(value)
    if (isNaN(numVal)) return

    if (numVal <= 0) {
      setAdjustmentError(t('INVALID_INPUT'))
      setDisposedStock(null)
      setDiscrepancyReason('')
    } else if (numVal > expectedStock) {
      setAdjustmentError(t('DISPOSAL_TOO_HIGH'))
      setDisposedStock(numVal)
    } else {
      setAdjustmentError(null)
      setDisposedStock(numVal)
      setDiscrepancyReason('')
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const finalDisposedStock = disposedStock ?? 0
      const newStock = expectedStock - finalDisposedStock
      const hasDiscrepancy = finalDisposedStock > expectedStock

      const data: DisposalData = {
        description: values.description || '',
        disposedStock: finalDisposedStock,
        newStock,
        hasDiscrepancy,
        discrepancyReason: hasDiscrepancy ? values.discrepancyReason || '' : '',
        actionedBy: values.actionedBy || '',
        actionedDate: values.actionedDate?.format('DD-MM-YYYY') || '',
        disposalMethod: values.disposalMethod || '',
        additionalDetails: showAdditionalDetails,
        media: fileList.map((file: any) => file.originFileObj).filter(Boolean)
      }
      onSave(data)
      form.resetFields()
      setDisposedStock(null)
      setAdjustmentError(null)
      setDiscrepancyReason('')
    })
  }

  const newStock =
    disposedStock !== null && disposedStock > 0
      ? expectedStock - disposedStock
      : null
  const hasWarning = disposedStock !== null && disposedStock > expectedStock
  const hasError = adjustmentError !== null && !hasWarning
  const newStockValue =
    disposedStock === null || disposedStock === 0 ? '' : newStock
  const showDiscrepancyReason = hasWarning

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <Disposal />
          <ModalTitle>
            <Heading level={3}>{t('DISPOSAL')}</Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <ModalContentContainer>
        <Form form={form}>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              full
              rules={[
                { required: true, message: t('DESCRIPTION_REQUIRED') },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="disposal-description"
              />
            </FormItem>
          </FormCard>
        </Form>

        <FormCard
          level={2}
          title={
            <CardTitleContainer>
              <MedicationNameHeading weight="bold" level={5}>
                {medicationName}
              </MedicationNameHeading>
            </CardTitleContainer>
          }
        >
          <AdjustmentContainer>
            <CalculationContainer>
              <CalculationColumn $align="center" $width="140px">
                <Label>{t('EXPECTED_STOCK')}</Label>
                <Value weight="bold">
                  {expectedStock} {t('TABLETS')}
                </Value>
              </CalculationColumn>
              <MinusIconWrapper>
                <MinusOutlined />
              </MinusIconWrapper>
              <CalculationColumn $flex>
                <LabelContainer>
                  <Asterix />
                  <Label>{t('DISPOSED_STOCK')}</Label>
                </LabelContainer>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={disposedStock ?? ''}
                  onChange={(e) => handleDisposedStockChange(e.target.value)}
                  status={
                    hasError ? 'error' : hasWarning ? 'warning' : undefined
                  }
                  style={{ width: '100%' }}
                  data-testid="disposed-stock-input"
                  suffix={t('TABLETS')}
                />
              </CalculationColumn>
              <EqualsIconWrapper>
                <EqualsSign>=</EqualsSign>
              </EqualsIconWrapper>
              <CalculationColumn $align="center" $width="140px">
                <Label>{t('NEW_STOCK')}</Label>
                <DiscrepancyValue
                  $isPositive={true}
                  $noDiscrepancy={true}
                  weight="bold"
                >
                  {newStockValue} {newStockValue ? t('TABLETS') : ''}
                </DiscrepancyValue>
              </CalculationColumn>
            </CalculationContainer>
            {hasError && (
              <StockBanner type="error">{adjustmentError}</StockBanner>
            )}
            {hasWarning && (
              <>
                <StockBanner type="warning">
                  {t('DISPOSAL_TOO_HIGH')}
                </StockBanner>
                <FormItem
                  name="discrepancyReason"
                  label={<Text>{t('REASON_FOR_DISCREPANCY')}</Text>}
                  required
                  full
                  rules={[
                    {
                      required: true,
                      message: t('REASON_FOR_DISCREPANCY_REQUIRED')
                    }
                  ]}
                >
                  <TextArea
                    rows={4}
                    value={discrepancyReason}
                    onChange={(e) => setDiscrepancyReason(e.target.value)}
                    placeholder={t('ENTER_REASON')}
                    maxLength={150}
                    showCount
                  />
                </FormItem>
              </>
            )}
          </AdjustmentContainer>
        </FormCard>

        <FormCard level={2}>
          <FormItem
            name="actionedBy"
            label={<Text>{t('ACTIONED_BY')}</Text>}
            required
            rules={[{ required: true, message: t('ACTIONED_BY_REQUIRED') }]}
          >
            <Select placeholder={t('SELECT_PERSON')} style={{ width: '100%' }}>
              <Select.Option value="user1">Richard K. Oates</Select.Option>
            </Select>
          </FormItem>

          <FormItem
            name="actionedDate"
            label={<Text>{t('ACTIONED_DATE')}</Text>}
            required
            rules={[{ required: true, message: t('ACTIONED_DATE_REQUIRED') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('SELECT_DATE')}
              format="DD-MM-YYYY"
            />
          </FormItem>

          <FormItem
            name="disposalMethod"
            label={
              <LabelWithIcon>
                <Text>{t('DISPOSAL_METHOD')}</Text>
                <Tooltip
                  title={t('DISPOSAL_METHOD_TOOLTIP')}
                  color={theme.color.neutral.color3}
                  overlayInnerStyle={{
                    color: '#fff',
                    borderRadius: '9px',
                    padding: '10px 16px'
                  }}
                >
                  <QuestionIcon>
                    <QuestionMarkIcon />
                  </QuestionIcon>
                </Tooltip>
              </LabelWithIcon>
            }
            required
            rules={[{ required: true, message: t('DISPOSAL_METHOD_REQUIRED') }]}
          >
            <Input placeholder={t('TYPE_HERE')} />
          </FormItem>
        </FormCard>

        <AdditionalDetailsSection
          showAdditionalDetails={showAdditionalDetails}
          onToggle={setShowAdditionalDetails}
          fileList={fileList}
          onFileChange={handleFileChange}
        />
      </ModalContentContainer>

      <Divider />

      <ButtonsContainer>
        <Button onClick={onCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            disposedStock === null ||
            disposedStock === 0 ||
            (hasWarning && !discrepancyReason)
          }
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CalculationColumn = styled.div<{
  $align?: 'left' | 'center'
  $width?: string
  $flex?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $flex }) => $flex && `flex: 1;`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
`

const MinusIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  width: 18px;
  height: 18px;

  .anticon {
    font-size: 18px;
    color: ${({ theme }) => theme.color.neutral.color1};
  }
`

const EqualsIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
`

const EqualsSign = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.neutral.color1};
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

export default DisposalModal
